import { getMainOrgUrl } from "app/client/models/gristUrlState";
import { getFinishSRPAuthParams, getStartSRPAuthParams } from "app/common/authUtils";
import { BaseAPI } from "app/common/BaseAPI";
import {
  AuthMethod,
  LoginResponse,
  LogInSecondStepParams,
  LoginSuccess,
  ResetPasswordResponse,
  SMSRegistrationInfo,
  SoftwareTokenRegistrationInfo,
  StartSRPAuthResponse,
  UserMFAPreferences,
  VerifyPasswordResponse,
  VerifyResult,
} from "app/common/LoginAPI";

export interface LogInOptions {
  preferredMfaMethod?: AuthMethod;
}

export interface LoginAPI {
  signUp(email: string, password: string, fullName?: string): Promise<void>;
  confirmSignUp(email: string, code: string): Promise<VerifyResult>;
  logIn(email: string, password: string, options?: LogInOptions): Promise<LoginResponse>;
  logInSecondStep(params: LogInSecondStepParams): Promise<LoginSuccess>;
  signInWithGoogle(): void;
  resetPassword(email: string): Promise<ResetPasswordResponse>;
  confirmResetPassword(username: string, password: string, code: string): Promise<void>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  getUserMfaPreferences(): Promise<UserMFAPreferences>;
  registerSoftwareToken(): Promise<SoftwareTokenRegistrationInfo>;
  confirmRegisterSoftwareToken(verificationCode: string): Promise<void>;
  unregisterSoftwareToken(): Promise<void>;
  registerSMS(phoneNumber: string): Promise<SMSRegistrationInfo>;
  confirmRegisterSMS(verificationCode: string): Promise<void>;
  unregisterSMS(): Promise<void>;
  verifyPassword(password: string, preferredMfaMethod?: AuthMethod): Promise<VerifyPasswordResponse>;
  verifySecondStep(authMethod: AuthMethod, verificationCode: string, session: string): Promise<void>;
}

export class LoginAPIImpl extends BaseAPI implements LoginAPI {
  private _url = getMainOrgUrl().replace(/\/$/, "");

  public async signUp(email: string, password: string, fullName?: string): Promise<void> {
    await this.request(`${this._url}/signup/register`, {
      method: "POST",
      body: JSON.stringify({ email, password, fullName }),
    });
  }

  public async confirmSignUp(email: string, code: string): Promise<VerifyResult> {
    return this.requestJson(`${this._url}/signup/verify`, {
      method: "POST",
      body: JSON.stringify({ email, code }),
    });
  }

  public async logIn(email: string, password: string, options: LogInOptions = {}): Promise<LoginResponse> {
    const { authHelper, clientAValue } = await getStartSRPAuthParams();
    const startAuthResponse: StartSRPAuthResponse = await this.requestJson(`${this._url}/api/auth/start-login`, {
      method: "POST",
      body: JSON.stringify({ clientAValue, email }),
    });

    const finishAuthParams = await getFinishSRPAuthParams(authHelper, { ...startAuthResponse, password });
    const finishLoginUrl = this._urlWithStateParams("/api/auth/finish-login");
    const { preferredMfaMethod } = options;
    return await this.requestJson(finishLoginUrl, {
      method: "POST",
      body: JSON.stringify({ ...finishAuthParams, preferredMfaMethod, username: startAuthResponse.userId }),
    });
  }

  public async logInSecondStep({
    username,
    verificationCode,
    authMethod,
    session,
  }: LogInSecondStepParams): Promise<LoginSuccess> {
    return await this.requestJson(
      this._urlWithStateParams("/api/auth/login-second-step"),
      {
        method: "POST",
        body: JSON.stringify({ username, authMethod, verificationCode, session }),
      },
    );
  }

  /**
   * Initiates the Sign in with Google authentication flow, which redirects
   * to a page on https://accounts.google.com for the user to select or sign
   * in to a Google account.
   */
  public signInWithGoogle(): void {
    window.location.assign(this._urlWithStateParams("/auth/google-sign-in"));
  }

  public async resetPassword(email: string): Promise<ResetPasswordResponse> {
    return this.requestJson(`${this._url}/api/auth/reset-password`, {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  public async confirmResetPassword(username: string, password: string, code: string): Promise<void> {
    await this.request(`${this._url}/api/auth/confirm-reset-password`, {
      method: "POST",
      body: JSON.stringify({ username, password, code }),
    });
  }

  public async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.request(`${this._url}/api/auth/change-password`, {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  public async getUserMfaPreferences(): Promise<UserMFAPreferences> {
    return this.requestJson(`${this._url}/api/profile/mfa_preferences`);
  }

  public async registerSoftwareToken(): Promise<SoftwareTokenRegistrationInfo> {
    return this.requestJson(`${this._url}/api/auth/register_totp`, { method: "POST" });
  }

  public async confirmRegisterSoftwareToken(verificationCode: string): Promise<void> {
    await this.request(`${this._url}/api/auth/confirm_register_totp`, {
      method: "POST",
      body: JSON.stringify({ verificationCode }),
    });
  }

  public async unregisterSoftwareToken(): Promise<void> {
    await this.request(`${this._url}/api/auth/unregister_totp`, { method: "POST" });
  }

  public async registerSMS(phoneNumber: string): Promise<SMSRegistrationInfo> {
    return this.requestJson(`${this._url}/api/auth/register_sms`, {
      method: "POST",
      body: JSON.stringify({ phoneNumber }),
    });
  }

  public async confirmRegisterSMS(verificationCode: string): Promise<void> {
    await this.request(`${this._url}/api/auth/confirm_register_sms`, {
      method: "POST",
      body: JSON.stringify({ verificationCode }),
    });
  }

  public async unregisterSMS(): Promise<void> {
    await this.request(`${this._url}/api/auth/unregister_sms`, { method: "POST" });
  }

  public async verifyPassword(password: string, preferredMfaMethod?: AuthMethod): Promise<VerifyPasswordResponse> {
    const { authHelper, clientAValue } = await getStartSRPAuthParams();
    const startAuthResponse: StartSRPAuthResponse = await this.requestJson(`${this._url}/api/auth/verify-password`, {
      method: "POST",
      body: JSON.stringify({ clientAValue }),
    });

    const finishAuthParams = await getFinishSRPAuthParams(authHelper, { ...startAuthResponse, password });
    return await this.requestJson(`${this._url}/api/auth/finish-verify-password`, {
      method: "POST",
      body: JSON.stringify({ ...finishAuthParams, preferredMfaMethod }),
    });
  }

  public async verifySecondStep(
    authMethod: AuthMethod,
    verificationCode: string,
    session: string,
  ): Promise<void> {
    await this.request(`${this._url}/api/auth/verify_second_step`, {
      method: "POST",
      body: JSON.stringify({ authMethod, verificationCode, session }),
    });
  }

  /**
   * Returns a login endpoint URL for the given `path` with the following
   * query parameters copied from the current URL:
   *
   *  - `state`: A key for login-related state (e.g. post-login redirect URL)
   *  - `cid`: Correlation identifier for an OAuth interaction
   */
  private _urlWithStateParams(path: string): string {
    const url = new URL(this._url + path);
    const params = new URLSearchParams(window.location.search);
    for (const name of ["state", "cid"]) {
      const value = params.get(name);
      if (value) { url.searchParams.set(name, value); }
    }
    return url.href;
  }
}
